'use client'
import NewsOutletCard from '@/app/[lng]/components/NewsOutlet/NewsOutletCard'
import NewsListingSkeleton from '@/app/[lng]/components/Skeletons/NewsListingSkeleton'
import CustomErrorBoundary from '@/app/[lng]/ErrorBoundary'
import { useGetNewsSearchQuery } from '@/app/globalRedux/api/searchSlice'
import { setNewsFilterData } from '@/app/globalRedux/filter/newsFilter'
import { RootState } from '@/app/globalRedux/store'
import { AdBannerScheduleType } from '@/app/types/adType'
import { NewsSearchResponse } from '@/app/types/newsSearchType'
import LimitPagination from '@/components/LimitPagination'
import { convertNaNToOne } from '@/lib/formatUtils'
import { useParams, usePathname, useRouter } from 'next/navigation'
import { useEffect, useOptimistic, useState, useTransition } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import InBetweenAds from '../../(AdComponent)/InBetweenAds'
import EmptyResult from '../../EmptyResult'
import { usePageNumberUpdator } from '@/app/hooks/usePageParamUpdater'
const adInterval = 3

const NewsList = ({ adList }: { adList?: AdBannerScheduleType[] }) => {
  const dispatch = useDispatch()
  const pathName = usePathname()
  const router = useRouter()
  const params = useParams()
  const [isPending, startTransition] = useTransition()
  const isNewsTab = pathName?.split('/')?.includes('news-tab')
  const commonFilter = useSelector((state: RootState) => state.commonFilter)
  const newsFilter = useSelector((state: RootState) => state.newsFilter)
  const search = useSelector((state: RootState) => state.searchData)
  const [adsData, setAdsData] = useState<AdBannerScheduleType[]>([])
  const goToPage = usePageNumberUpdator()
  const [optimisticPageNo, setOptimisticPageNo] = useOptimistic<number>(
    convertNaNToOne(Number(params?.pageNO?.[0] || 1))
  )
  const {
    data: newsData,
    isFetching: isNewsDataFetching,
    isError: isNewsError,
  } = useGetNewsSearchQuery<{
    data: NewsSearchResponse
    isFetching: boolean
    isError: boolean
    isLoading: boolean
    isSuccess: boolean
  }>(
    {
      newsFilter: newsFilter,
      commonFilter: commonFilter,
      pageNO: convertNaNToOne(Number(params?.pageNO?.[0] || 1)),
    },

    {
      skip: newsFilter.isNewsRouteLoading,
    }
  )

  const handlePageChange = (page: number) => {
    startTransition(() => {
      setOptimisticPageNo(page)
      goToPage({ newPageNo: page })
    })
  }

  const isNewsLoading = () => {
    return (
      isNewsDataFetching ||
      isNewsError ||
      (params?.pageNO?.[0] !== '1' && !newsData?.news?.length)
    )
  }

  useEffect(() => {
    if (adList) {
      const adFilter5 =
        adList.filter(
          (ad) =>
            ad.ad_position.name ===
            `${!isNewsTab ? 'Ad Position 4' : 'Ad Position 5'}`
        )[0] ?? {}
      const adFilter6 =
        adList.filter(
          (ad) =>
            ad.ad_position.name ===
            `${!isNewsTab ? 'Ad Position 5' : 'Ad Position 6'}`
        )[0] ?? {}
      const adFilter7 =
        adList.filter(
          (ad) =>
            ad.ad_position.name ===
            `${!isNewsTab ? 'Ad Position 6' : 'Ad Position 7'}`
        )[0] ?? {}

      const adsData = [adFilter5, adFilter6, adFilter7]

      setAdsData(adsData)
    }
  }, [adList])
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }, [pathName])

  useEffect(() => {
    if (
      search?.searchTerm?.trim()?.length === 0 &&
      newsData?.news?.length == 0
    ) {
      router.push(`/${params.lng}/news/1`)
    }
  }, [newsData])

  useEffect(() => {
    dispatch(setNewsFilterData({ isNewsRouteLoading: isPending }))
  }, [isPending])

  useEffect(() => {
    const currentUrl = new URL(window.location.href)
    const pageNum = !isNaN(Number(pathName?.split('/')?.at(-1)))
    if (!pageNum) {
      const urlWithPageNo = `${pathName}/1${currentUrl.search}`
      router.replace(urlWithPageNo)
    }
  }, [])

  return (
    <div className="flex flex-col gap-5 mobile:gap-[10px]">
      {isNewsLoading() ? (
        <NewsListingSkeleton />
      ) : newsData?.news?.length > 0 ? (
        newsData.news.map((newsItem, index) => (
          <CustomErrorBoundary key={newsItem.id}>
            <div
              key={`${newsItem.id}+newsList`}
              className="flex flex-col gap-4 "
            >
              <NewsOutletCard newsData={newsItem} />
              {(index + 1) % adInterval === 0 && (
                <InBetweenAds
                  index={index}
                  adsData={
                    adsData[Math.floor(index / adInterval) % adsData.length]
                  }
                />
              )}
            </div>
          </CustomErrorBoundary>
        ))
      ) : (
        <EmptyResult searchTerm={newsFilter?.searchTerm ?? ''} />
      )}

      {newsData?.news && newsData?.news?.length > 0 && (
        <div className=" mt-[35px] border-t-2 border-t-[#e6e6e6] border-solid mobile:mt-[10px]">
          <LimitPagination
            currentPage={optimisticPageNo}
            pageCount={Math.ceil(newsData?.count / 10) || 1}
            visiblePages={5}
            onPageChange={(page) => {
              handlePageChange(page)
            }}
          />
        </div>
      )}
    </div>
  )
}

export default NewsList
